import React, { useEffect, useRef } from "react";
import {
  GraduationCap,
  CircleDollarSign,
  FileText,
  CalendarCheck,
  Bus,
  Users,
  Building,
  Book,
  Bed,
  TrendingUp,
  Briefcase,
  Star,
  School,
  CheckCircle,
  Zap,
  ShieldCheck,
  Sparkles,
  Award
} from "lucide-react";
import SEO from "../components/SEO";
import "./ICampusBOAT.css";

const SectionZigZag = ({ icon, title, content, img, reverse, index, cardRefs, handleMouseMove, handleMouseLeave }) => (
  <div className={`boat-zigzag-row ${reverse ? "row-reverse-b" : ""} beat-reveal-scroll`}>
    <div
      ref={el => cardRefs.current[index] = el}
      className="boat-interactive-card"
      onMouseMove={(e) => handleMouseMove(e, index)}
      onMouseLeave={() => handleMouseLeave(index)}
    >
      <div className="boat-card-shimmer"></div>
      <div className="boat-card-inner">
        <div className="boat-card-head">
          <div className="icon-wrap-b">{icon}</div>
          <h2 className="card-title-b">{title}</h2>
        </div>
        <ul className="feat-list-b">
          {content.map((line, i) => (
            <li key={i}><CheckCircle size={14} className="check-b" /> {line}</li>
          ))}
        </ul>
      </div>
    </div>
    <div className="boat-image-container beat-reveal-scroll">
      <div className="img-glass-frame">
        <img src={img} alt={title} className="boat-feature-img" />
      </div>
    </div>
  </div>
);

const ICampusBOAT = () => {
  const observerRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-beat-in-view');
        }
      });
    }, { threshold: 0.1 });

    const elements = document.querySelectorAll('.beat-reveal-scroll');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const handleMouseMove = (e, index) => {
    const card = cardRefs.current[index];
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;

    // Shimmer 
    const shimmer = card.querySelector('.boat-card-shimmer');
    if (shimmer) {
      shimmer.style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(230,137,0,0.15) 0%, transparent 80%)`;
    }
  };

  const handleMouseLeave = (index) => {
    const card = cardRefs.current[index];
    if (!card) return;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
    const shimmer = card.querySelector('.boat-card-shimmer');
    if (shimmer) {
      shimmer.style.background = `transparent`;
    }
  };

  const sections = [
    {
      icon: <GraduationCap size={28} />,
      title: "Admissions",
      img: "./images/campusimages/admissions.png",
      content: [
        "User friendly, multi-node admission process",
        "Instant photo capture via webcam",
        "Flexible branch changes and re-admissions",
        "Auto Register No. & section allotment",
        "Generate course expenditure certificates",
      ]
    },
    {
      icon: <CircleDollarSign size={28} />,
      title: "Accounts & Fee",
      img: "./images/campusimages/accounts-fee.png",
      reverse: true,
      content: [
        "Fee Master by admission mode/category",
        "Unified collection (Academic, Transport, Hostel)",
        "Bank-wise challans with offline support",
        "View dues by academic/financial year",
        "Seamless fee concessions & refund workflows",
      ]
    },
    {
      icon: <FileText size={28} />,
      title: "Examinations",
      img: "./images/campusimages/Post-exam.png",
      content: [
        "Internal marks entry with freeze/edit rights",
        "Custom calculation of final internal marks",
        "Enhanced secure result processing",
        "Eliminate manual errors, ensure integrity",
      ]
    },
    {
      icon: <ShieldCheck size={28} />,
      title: "Student Discipline",
      img: "./images/campusimages/discipline.png",
      reverse: true,
      content: [
        "View student details instantly via dashboard",
        "Attendance, fee, library & hostel info",
        "Academic performance tracking across modules",
        "100% accurate student history records",
      ]
    },
    {
      icon: <CalendarCheck size={28} />,
      title: "Attendance",
      img: "./images/campusimages/timetable-attendance.png",
      content: [
        "Dynamic faculty and class timetables",
        "Multiple subjects/faculty per period handling",
        "Batch creation & period adjustments",
        "Optional Biometric integration",
        "Hassle-free mobile attendance marking",
      ]
    },
    {
      icon: <Bus size={28} />,
      title: "Transport",
      img: "./images/campusimages/transport.png",
      reverse: true,
      content: [
        "Route, vehicle & driver management",
        "Smart student allocation & deallocation",
        "Vehicle meter tracking & fee integration",
        "Live route monitoring & alerts",
      ]
    },
    {
      icon: <Users size={28} />,
      title: "Alumni Network",
      img: "./images/campusimages/alumni.png",
      content: [
        "One-click alumni database creation",
        "Sign-up, job postings & photo sharing",
        "Authority approval flows for joining",
        "High alumni-student interaction tools",
      ]
    },
    {
      icon: <Building size={28} />,
      title: "HR / Payroll",
      img: "./images/campusimages/hr-payroll.png",
      reverse: true,
      content: [
        "Centralized salary structure config",
        "EPF/ESI slabs & automated CSV export",
        "Multi-level leave approval workflows",
        "Secure employee account management",
      ]
    },
    {
      icon: <Book size={28} />,
      title: "Library",
      img: "./images/campusimages/library.png",
      content: [
        "Classified book indexing & circulation",
        "Subject-wise holdings & overdue notices",
        "Integrated budget analysis tools",
        "Automatic overdue reminders to students",
      ]
    },
    {
      icon: <Bed size={28} />,
      title: "Hostels",
      img: "./images/campusimages/hostels.png",
      reverse: true,
      content: [
        "Room categorization & transfer management",
        "Visitor logs, outing records & complaints",
        "Daily attendance and fee integration",
        "Integrated room concession workflows",
      ]
    },
    {
      icon: <TrendingUp size={28} />,
      title: "Performance",
      img: "./images/campusimages/performance.png",
      content: [
        "One-click full teaching profile view",
        "Student feedback-driven teaching ratings",
        "Teaching appraisal & attendance tracking",
        "Cancelled/adjusted period monitoring",
      ]
    },
    {
      icon: <Briefcase size={28} />,
      title: "Placements",
      img: "./images/campusimages/training-placement.png",
      reverse: true,
      content: [
        "Recruiter & interview record tracking",
        "SMS alerts to parents for selections",
        "Course-wise placement statistics",
        "Student interview attendance logs",
      ]
    },
    {
      icon: <Star size={28} />,
      title: "Highlights",
      img: "./images/campusimages/highlights.png",
      content: [
        "Real-time, fully automated ERP",
        "SMS, WhatsApp & Email Notifications",
        "Unified database & design consistency",
        "Role-based control, secure at every level",
        "100% e-enabled communication platform",
      ]
    }
  ];

  return (
    <div className="premium-boat-wrapper">
      <SEO
        title="iCampus BOAT — Campus ERP & Governance Platform | DBS"
        description="Streamline institution-wide operations with iCampus BOAT ERP: admissions, fee management, HR/payroll, transport, hostel, and digital governance for universities and colleges."
        keywords="iCampus BOAT, university ERP software, campus management system, student fee management ERP, college governance software India"
        canonical="https://dbasesolutions.in/icampus-boat"
        ogImage="/og-icampusboat.png"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Products", url: "/products" },
          { name: "iCampus BOAT ERP", url: "/icampus-boat" }
        ]}
      />
      {/* Fluid Background */}
      <div className="boat-fluid-bg">
        <div className="boat-orb o-b1"></div>
        <div className="boat-orb o-b2"></div>
        <div className="boat-grid-pattern"></div>
      </div>

      <div className="boat-container-main">
        <header className="boat-hero-header beat-reveal-scroll">
          <div className="boat-header-tag">
            <Zap size={14} className="tag-flash" />
            <span>DBS Enterprise Solution</span>
          </div>
          <div className="boat-logo-wrapper">
            <img src="./images/icampus_boat.png" alt="iCampus BOAT" className="boat-logo-3d" />
          </div>
          <h1 className="boat-title-main">
            iCampus <span className="text-fusion-b">BOAT</span>
          </h1>
          <p className="boat-subtitle-main">
            Automate Academic, Administrative, and Operational Workflows with DBS iCampus BOAT ERP Management Suite.
          </p>
          <div className="boat-hero-decor">
            <Sparkles className="d-b b1" />
            <ShieldCheck className="d-b b2" />
            <Award className="d-b b3" />
          </div>
        </header>

        <section className="boat-overview-section beat-reveal-scroll">
          <div className="overview-glass-b">
            <div className="over-head-b">
              <School size={32} color="#e68900" />
              <h2>Integrated ERP Solution</h2>
            </div>
            <p>
              DBS iCampus BOAT provides a 100% accurate mechanism for the automated processing of entire institution data. It ensures that information is always up-to-date, reducing errors and providing histories at a single click.
            </p>
          </div>
        </section>

        <section className="boat-features-grid">
          {sections.map((sec, idx) => (
            <SectionZigZag
              key={idx}
              index={idx}
              icon={sec.icon}
              title={sec.title}
              img={sec.img}
              content={sec.content}
              reverse={sec.reverse}
              cardRefs={cardRefs}
              handleMouseMove={handleMouseMove}
              handleMouseLeave={handleMouseLeave}
            />
          ))}
        </section>

        <footer className="boat-final-cta beat-reveal-scroll">
          <div className="cta-box-b">
            <h3>Secure Your Institution Today</h3>
            <p>Join the future of automated campus management with BOAT ERP.</p>
            <button className="btn-primary-b">Connect With Us</button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ICampusBOAT;
